import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import tr from './locales/tr.json';

export const LANGUAGES = ['tr', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];

export const resources = { tr: { translation: tr }, en: { translation: en } } as const;

const STORAGE_KEY = 'corocanto.language';

function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

/** The language last used in this browser (so the login page already speaks it); Turkish first. */
function storedLanguage(): Language {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isLanguage(value) ? value : 'tr';
  } catch {
    return 'tr';
  }
}

void i18n.use(initReactI18next).init({
  resources,
  lng: storedLanguage(),
  fallbackLng: 'tr',
  interpolation: { escapeValue: false },
  returnNull: false,
  // Resources are bundled, so initialize synchronously: i18n.language is set before the first
  // render (components that compare it, like the language switch, would otherwise race).
  initAsync: false,
});
document.documentElement.lang = i18n.language;

/**
 * Switch the interface language and remember it in this browser. The signed-in user's choice is
 * also saved on the server (PATCH /api/me), which wins after every login.
 */
export function applyLanguage(language: string): void {
  if (!isLanguage(language)) return;
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Storage unavailable: the server-side preference still applies after login.
  }
  document.documentElement.lang = language;
  if (i18n.language !== language) void i18n.changeLanguage(language);
}

/** The locale for dates and numbers in the current language. */
export function dateLocale(): string {
  return i18n.language === 'en' ? 'en-US' : 'tr-TR';
}

export default i18n;
